import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

// Lightweight status probe for the Integrations section on Settings.
// Returns connection state + row count + last sync for each external system.
export function useIntegrationsStatus() {
  const [stripe, setStripe] = useState({ loading: true, connected: null, count: 0, lastSyncedAt: null, error: null });
  const [qb, setQb] = useState({ loading: true, connected: null, error: null });

  const probeStripe = useCallback(async () => {
    setStripe((s) => ({ ...s, loading: true, error: null }));
    try {
      const { count, error: countErr } = await supabase
        .from("stripe_charges")
        .select("*", { count: "exact", head: true });
      if (countErr) throw countErr;
      const { data: newest, error: newestErr } = await supabase
        .from("stripe_charges")
        .select("updated_at")
        .order("updated_at", { ascending: false })
        .limit(1);
      if (newestErr) throw newestErr;
      setStripe({
        loading: false,
        connected: (count || 0) > 0,
        count: count || 0,
        lastSyncedAt: newest?.[0]?.updated_at || null,
        error: null,
      });
    } catch (e) {
      setStripe({ loading: false, connected: false, count: 0, lastSyncedAt: null, error: e.message || "Probe failed" });
    }
  }, []);

  const probeQb = useCallback(async () => {
    setQb((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) { setQb({ loading: false, connected: false, error: null }); return; }
      const today = new Date().toISOString().slice(0, 10);
      const res = await fetch(`/api/qb-transactions?since=${today}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) { setQb({ loading: false, connected: false, error: null }); return; }
      const data = await res.json();
      setQb({ loading: false, connected: !!data.connected, error: null });
    } catch (e) {
      setQb({ loading: false, connected: false, error: e.message || "Probe failed" });
    }
  }, []);

  useEffect(() => {
    probeStripe();
    probeQb();
  }, [probeStripe, probeQb]);

  return { stripe, qb, refreshStripe: probeStripe, refreshQb: probeQb };
}
